/**
 * stochasticMath.js
 *
 * Slow Stochastic Oscillator (%K, %D) + cross detection for the
 * Liquidity Map. 100% pure functions — no I/O, no React.
 *
 * Formula (Slow Stochastic, K=14 · smooth=3 · D=3):
 *   raw_K[i] = ((Close[i] - LowestLow_N) / (HighestHigh_N - LowestLow_N)) * 100
 *   %K       = SMA(raw_K, smooth)   // "slow" smoothing
 *   %D       = SMA(%K, dPeriod)     // signal line
 *
 * Key zones:
 *   overbought > 80
 *   oversold   < 20
 *   neutral    20-80
 *
 * Crosses only emitted when they occur inside an extreme zone
 * (bullish cross < 20 · bearish cross > 80) — neutral-zone crosses
 * are pure noise and are suppressed.
 */

const DEFAULTS = { kPeriod: 14, dPeriod: 3, smooth: 3 };

/**
 * Compute Slow Stochastic %K and %D series for a candles array.
 * Returns two arrays of the same length as `candles`; leading slots
 * where the window can't be filled are NaN.
 *
 * @param {Array<{h:number,l:number,c:number}>} candles
 * @param {{kPeriod?:number, dPeriod?:number, smooth?:number}} [opts]
 * @returns {{k:number[], d:number[]}}
 */
export function computeStochastic(candles, opts = {}) {
  const kPeriod = opts.kPeriod ?? DEFAULTS.kPeriod;
  const dPeriod = opts.dPeriod ?? DEFAULTS.dPeriod;
  const smooth  = opts.smooth  ?? DEFAULTS.smooth;
  const n = Array.isArray(candles) ? candles.length : 0;
  const kOut = new Array(n).fill(NaN);
  const dOut = new Array(n).fill(NaN);
  if (n < kPeriod) return { k: kOut, d: dOut };

  // ── Step 1: raw %K per candle ────────────────────────────────
  const rawK = new Array(n).fill(NaN);
  for (let i = kPeriod - 1; i < n; i++) {
    let hh = -Infinity, ll = Infinity;
    for (let j = i - kPeriod + 1; j <= i; j++) {
      const h = candles[j]?.h, l = candles[j]?.l;
      if (Number.isFinite(h) && h > hh) hh = h;
      if (Number.isFinite(l) && l < ll) ll = l;
    }
    const close = candles[i]?.c;
    if (!Number.isFinite(close) || !Number.isFinite(hh) || !Number.isFinite(ll)) continue;
    const range = hh - ll;
    // Degenerate flat window ⇒ neutral 50 (avoids div/0).
    rawK[i] = range > 0 ? ((close - ll) / range) * 100 : 50;
  }

  // ── Step 2: %K = SMA(rawK, smooth) ───────────────────────────
  for (let i = kPeriod - 1 + (smooth - 1); i < n; i++) {
    let sum = 0, cnt = 0;
    for (let j = i - smooth + 1; j <= i; j++) {
      const v = rawK[j];
      if (Number.isFinite(v)) { sum += v; cnt++; }
    }
    if (cnt === smooth) kOut[i] = sum / cnt;
  }

  // ── Step 3: %D = SMA(%K, dPeriod) ────────────────────────────
  for (let i = kPeriod - 1 + (smooth - 1) + (dPeriod - 1); i < n; i++) {
    let sum = 0, cnt = 0;
    for (let j = i - dPeriod + 1; j <= i; j++) {
      const v = kOut[j];
      if (Number.isFinite(v)) { sum += v; cnt++; }
    }
    if (cnt === dPeriod) dOut[i] = sum / cnt;
  }

  return { k: kOut, d: dOut };
}

/**
 * Classify a %K value into a zone.
 * @param {number} k
 * @returns {"overbought"|"oversold"|"neutral"|null}
 */
export function stochasticZone(k) {
  if (!Number.isFinite(k)) return null;
  if (k >= 80) return "overbought";
  if (k <= 20) return "oversold";
  return "neutral";
}

/**
 * Detect %K × %D crosses that occurred INSIDE an extreme zone in the
 * last `lookback` candles. Neutral-zone crosses are ignored.
 *
 * Returns an array (chronological) of:
 *   { idx: number, ts: number|null, dir: "bull"|"bear",
 *     zone: "overbought"|"oversold", k: number, d: number }
 *
 * @param {Array<{t?:number}>} candles
 * @param {number[]} kArr
 * @param {number[]} dArr
 * @param {number} [lookback=200]
 */
export function detectStochasticCrosses(candles, kArr, dArr, lookback = 200) {
  const out = [];
  const n = Math.min(candles?.length || 0, kArr?.length || 0, dArr?.length || 0);
  if (n < 2) return out;
  const start = Math.max(1, n - lookback);
  for (let i = start; i < n; i++) {
    const kA = kArr[i], dA = dArr[i], kB = kArr[i - 1], dB = dArr[i - 1];
    if (!Number.isFinite(kA) || !Number.isFinite(dA) ||
        !Number.isFinite(kB) || !Number.isFinite(dB)) continue;
    const cur = kA - dA;
    const prev = kB - dB;
    if (cur === 0 || prev === 0) continue;
    if ((cur > 0) === (prev > 0)) continue; // no sign flip
    const dir = cur > 0 ? "bull" : "bear";
    // Extreme-zone filter — use the CURRENT %K (post-cross) as the
    // classifier. Bull crosses only in oversold, bear only in overbought.
    if (dir === "bull" && kA > 20) continue;
    if (dir === "bear" && kA < 80) continue;
    const zone = dir === "bull" ? "oversold" : "overbought";
    out.push({
      idx: i,
      ts: candles[i]?.t ?? null,
      dir,
      zone,
      k: kA,
      d: dA,
    });
  }
  return out;
}

/**
 * Convenience: latest %K / %D + zone snapshot, useful for the label
 * in the subpanel's top-right corner.
 * @param {number[]} kArr
 * @param {number[]} dArr
 */
export function stochasticSnapshot(kArr, dArr) {
  const n = Math.min(kArr?.length || 0, dArr?.length || 0);
  if (n === 0) return { k: null, d: null, zone: null };
  // Walk backward to the last finite pair.
  for (let i = n - 1; i >= 0; i--) {
    if (Number.isFinite(kArr[i]) && Number.isFinite(dArr[i])) {
      return { k: kArr[i], d: dArr[i], zone: stochasticZone(kArr[i]) };
    }
  }
  return { k: null, d: null, zone: null };
}

export const STOCHASTIC_DEFAULTS = DEFAULTS;
